import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  MedicalServices as MedicalIcon,
  HealthAndSafety as HealthIcon,
  Medication as MedicationIcon,
  MonitorWeight as WeightIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import {
  usePatientAllergies,
  usePatientMedicalProfile,
  useUpdatePatientMedicalProfile,
  getAllergyDisplayState,
  type PatientMedicalProfile,
} from '../../hooks/queries/usePatientAllergies';
import fdbService from '../../services/fdbService';

export interface MedicalProfileDialogProps {
  open: boolean;
  onClose: () => void;
  /** Composite patient id: `{pharmacyId}_{patientId}`. */
  patientId: string;
  /**
   * PRX-80 Task 9 fix round 1 (item 1) — additive, default false. Existing
   * callers (`PatientAllergiesCard`, `MedicalProfileHeaderSummary`'s own
   * pre-existing mount in `PatientHeader.tsx`) pass neither this prop and are
   * byte-for-byte unaffected. When true, disables every field and the Save
   * button — the dialog can still be opened to VIEW the profile, but its save
   * path can never be reached.
   */
  readOnly?: boolean;
}

/** Case-insensitive membership test used to avoid adding duplicate entries. */
const includesCi = (list: string[], value: string) =>
  list.some((v) => v.toLowerCase() === value.toLowerCase());

/** Minimum characters before we query FDB for suggestions (matches the API's guard). */
const SUGGEST_MIN_CHARS = 2;
const SUGGEST_DEBOUNCE_MS = 250;

/**
 * An editable chip list whose add-input offers coded suggestions from FDB
 * (GP-1135): allergens from the FDB allergy pick list — the same concept
 * vocabulary Kroll's own allergy records use — and conditions from the FDBDX
 * master. freeSolo keeps free text possible for anything not in the
 * vocabulary. Purely local state; the parent owns persistence.
 */
const EditableList: React.FC<{
  title: string;
  icon: React.ReactNode;
  placeholder: string;
  items: string[];
  emptyText: string;
  chipColor: 'error' | 'warning' | 'default';
  /** Blocks input while the persisted profile is still loading (see dialog). */
  disabled?: boolean;
  fetchSuggestions: (q: string) => Promise<string[]>;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
}> = ({ title, icon, placeholder, items, emptyText, chipColor, disabled = false, fetchSuggestions, onAdd, onRemove }) => {
  const [draft, setDraft] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Debounced FDB suggestion fetch. Stale responses are ignored via the
  // cancelled flag so a fast typist never sees out-of-order results.
  useEffect(() => {
    const q = draft.trim();
    if (q.length < SUGGEST_MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await fetchSuggestions(q);
        if (!cancelled) setOptions(results);
      } catch {
        // Suggestions are an enhancement — free text still works, so a failed
        // lookup degrades to an empty dropdown rather than an error.
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft, fetchSuggestions]);

  const commit = (value?: string) => {
    const v = (value ?? draft).trim();
    if (!v) return;
    onAdd(v);
    setDraft('');
    setOptions([]);
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, minHeight: 32 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : (
          items.map((item) => (
            <Chip
              key={item}
              label={item}
              color={chipColor === 'default' ? undefined : chipColor}
              variant={chipColor === 'default' ? 'outlined' : 'filled'}
              onDelete={() => onRemove(item)}
              size="small"
            />
          ))
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          disabled={disabled}
          options={options}
          loading={loading}
          // Fully controlled with value pinned to null: this box is a one-shot
          // picker that pushes the chosen term into the chip list and resets.
          // Holding no selection stops MUI from re-emitting the previously
          // chosen option (e.g. on blur), which was calling onAdd twice and
          // raising a false "already on the list" toast (GP-1151).
          value={null}
          inputValue={draft}
          onInputChange={(_e, value, reason) => {
            // 'reset' fires right after a selection to clear the box; commit()
            // already clears the draft, so ignore it to avoid churn.
            if (reason !== 'reset') setDraft(value);
          }}
          // Selecting a suggestion (click or Enter on a highlighted option)
          // commits immediately; plain Enter on free text also lands here.
          onChange={(_e, value) => {
            if (typeof value === 'string' && value.trim()) commit(value);
          }}
          // The API already filters server-side; don't re-filter client-side
          // (MUI's default filter would hide e.g. "penicillin G" for "pen g").
          filterOptions={(x) => x}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                maxLength: 200,
                'aria-label': placeholder,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => commit()}
          disabled={disabled || !draft.trim()}
          sx={{ flexShrink: 0 }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
};

/**
 * Reusable Allergies & Medical Conditions editor dialog (GP-1151). One shared
 * surface for Patient 360, Counseling, and Clinical Verification — anywhere a
 * patient's PHOX-owned medical profile needs to be viewed or amended.
 *
 * PHOX-recorded entries persist to the local patient row only — Kroll is never
 * written, and no live allergy/interaction checks run on entry. Both facts are
 * stated explicitly in the dialog so staff never mistake this for a clinical
 * safety check (the video ask on GP-1151).
 */
const MedicalProfileDialog: React.FC<MedicalProfileDialogProps> = ({ open, onClose, patientId, readOnly = false }) => {
  const [pharmacyId, patientNumber] = useMemo(() => {
    const [pid, num] = (patientId || '').split('_');
    return [pid, num];
  }, [patientId]);

  const { data: profile, isLoading } = usePatientMedicalProfile(patientId, { enabled: open });
  const { data: krollAllergyStatus } = usePatientAllergies(pharmacyId, patientNumber, {
    enabled: open,
  });
  const updateMutation = useUpdatePatientMedicalProfile(patientId);

  const [allergies, setAllergies] = useState<string[]>([]);
  const [conditions, setConditions] = useState<string[]>([]);
  const [medications, setMedications] = useState<string[]>([]);
  const [weight, setWeight] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  // The exact profile object we last seeded local state from, so we only reseed
  // when a genuinely new fetch arrives — never on an unrelated re-render.
  const seededProfileRef = useRef<PatientMedicalProfile | null>(null);

  // Each open starts a fresh editing session: drop the previous seed and any
  // unsaved edits so the dialog always opens showing the persisted profile.
  useEffect(() => {
    if (open) {
      seededProfileRef.current = null;
      setDirty(false);
    }
  }, [open]);

  // Seed local editable state from the saved profile whenever it (re)loads.
  // Skip while the user has unsaved edits so a background refetch can't
  // clobber them; the reference guard stops a bare `dirty` flip from reseeding
  // out of the stale cached profile (GP-1151).
  useEffect(() => {
    if (!open || !profile || dirty) return;
    if (seededProfileRef.current === profile) return;
    seededProfileRef.current = profile;
    setAllergies(profile.allergies || []);
    setConditions(profile.medicalConditions || []);
    setMedications(profile.medications || []);
    setWeight(profile.weight || '');
  }, [open, profile, dirty]);

  const addAllergy = (v: string) => {
    if (includesCi(allergies, v)) {
      enqueueSnackbar(`"${v}" is already on the allergy list`, { variant: 'info' });
      return;
    }
    setAllergies((prev) => [...prev, v]);
    setDirty(true);
  };
  const removeAllergy = (v: string) => {
    setAllergies((prev) => prev.filter((x) => x !== v));
    setDirty(true);
  };
  const addCondition = (v: string) => {
    if (includesCi(conditions, v)) {
      enqueueSnackbar(`"${v}" is already on the conditions list`, { variant: 'info' });
      return;
    }
    setConditions((prev) => [...prev, v]);
    setDirty(true);
  };
  const removeCondition = (v: string) => {
    setConditions((prev) => prev.filter((x) => x !== v));
    setDirty(true);
  };
  const addMedication = (v: string) => {
    if (includesCi(medications, v)) {
      enqueueSnackbar(`"${v}" is already on the medication list`, { variant: 'info' });
      return;
    }
    setMedications((prev) => [...prev, v]);
    setDirty(true);
  };
  const removeMedication = (v: string) => {
    setMedications((prev) => prev.filter((x) => x !== v));
    setDirty(true);
  };

  const handleSave = () => {
    // Never persist before the saved profile has seeded local state — a save
    // from the pre-load empty lists would overwrite the server's lists with
    // whatever little the user typed during the load window (Codex review).
    if (!profile) return;
    updateMutation.mutate(
      {
        allergies,
        medicalConditions: conditions,
        medications,
        weight: weight.trim() || null,
      },
      {
        onSuccess: (saved) => {
          // Adopt the server's normalized, persisted lists as the new baseline
          // immediately, so the just-saved chips stay put regardless of when
          // the background refetch lands (GP-1151).
          setAllergies(saved.allergies || []);
          setConditions(saved.medicalConditions || []);
          setMedications(saved.medications || []);
          setWeight(saved.weight || '');
          setDirty(false);
          enqueueSnackbar('Medical profile saved', { variant: 'success' });
        },
        onError: (err: any) => {
          enqueueSnackbar(
            err?.response?.data?.message || 'Failed to save medical profile',
            { variant: 'error' },
          );
        },
      },
    );
  };

  // Read-only summary of what Kroll reports, so both sources are visible at once.
  const krollNote = useMemo(() => {
    const state = getAllergyDisplayState(krollAllergyStatus);
    if (!state) return null;
    if (state === 'has-allergies') {
      const list = (krollAllergyStatus?.allergies || [])
        .map((a) => a.description?.trim())
        .filter((d): d is string => !!d);
      return list.length
        ? `Kroll: ${list.join(', ')}`
        : 'Kroll: allergy rows present but undescribed — verify with patient';
    }
    if (state === 'nka-confirmed') return 'Kroll: No Known Allergies (confirmed)';
    return 'Kroll: no allergy information on file — verify with patient';
  }, [krollAllergyStatus]);

  const fetchAllergenSuggestions = useMemo(
    () => async (q: string) => (await fdbService.searchAllergens(q)).map((a) => a.description),
    [],
  );
  const fetchConditionSuggestions = useMemo(
    () => async (q: string) => (await fdbService.searchConditions(q)).map((c) => c.fdbdxDesc),
    [],
  );
  // BPMH is free text — there is no FDB drug-name pick list, so no suggestions.
  const fetchMedicationSuggestions = useMemo(() => async (_q: string) => [] as string[], []);

  // While there are unsaved edits, backdrop/Escape must not silently discard
  // them — closing then requires the explicit "Discard changes" button.
  const handleDialogClose = (_e: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (dirty && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <HealthIcon color="primary" />
          <Typography variant="h6" fontWeight={600} component="span">
            Allergies &amp; Medical Conditions
          </Typography>
          {isLoading && <CircularProgress size={16} />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {/* The one place this caveat lives (GP-1151): PHOX-only record. */}
        <Alert severity="info" sx={{ mb: 2 }}>
          PHOX-only record — nothing entered here is written back to Kroll, and no live
          allergy or drug-interaction checks are performed. Kroll drives dispensing checks —
          record allergies in Kroll as well.
        </Alert>

        {krollNote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {krollNote}
          </Typography>
        )}

        {/* Patient weight — surfaced for at-a-glance clinical assessment (GP-1174). */}
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <WeightIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Weight
            </Typography>
          </Stack>
          <TextField
            size="small"
            disabled={!profile || readOnly}
            value={weight}
            onChange={(e) => {
              setWeight(e.target.value);
              setDirty(true);
            }}
            placeholder="e.g. 72 kg"
            inputProps={{ maxLength: 50, 'aria-label': 'Patient weight' }}
            sx={{ width: { xs: '100%', sm: 240 } }}
          />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          divider={
            <Divider
              flexItem
              orientation="vertical"
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
          }
          spacing={3}
        >
          <EditableList
            title="Allergies"
            icon={<WarningIcon color="error" fontSize="small" />}
            placeholder="Add an allergy (e.g. Penicillin)"
            items={allergies}
            emptyText={profile ? 'No PHOX-registered allergies' : 'Loading saved profile…'}
            chipColor="error"
            disabled={!profile || readOnly}
            fetchSuggestions={fetchAllergenSuggestions}
            onAdd={addAllergy}
            onRemove={removeAllergy}
          />
          <EditableList
            title="Medical Conditions"
            icon={<MedicalIcon color="action" fontSize="small" />}
            placeholder="Add a condition (e.g. Hypertension)"
            items={conditions}
            emptyText={profile ? 'No PHOX-registered medical conditions' : 'Loading saved profile…'}
            chipColor="default"
            disabled={!profile || readOnly}
            fetchSuggestions={fetchConditionSuggestions}
            onAdd={addCondition}
            onRemove={removeCondition}
          />
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* Medication list / BPMH — best possible medication history (GP-1174). */}
        <EditableList
          title="Medication List (BPMH)"
          icon={<MedicationIcon color="primary" fontSize="small" />}
          placeholder="Add a medication (e.g. Metformin 500mg BID)"
          items={medications}
          emptyText={profile ? 'No PHOX-registered medications' : 'Loading saved profile…'}
          chipColor="default"
          disabled={!profile || readOnly}
          fetchSuggestions={fetchMedicationSuggestions}
          onAdd={addMedication}
          onRemove={removeMedication}
        />
      </DialogContent>
      <DialogActions>
        {dirty && (
          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, mr: 1 }}>
            Unsaved changes
          </Typography>
        )}
        <Button onClick={onClose} color={dirty ? 'warning' : 'inherit'}>
          {dirty ? 'Discard changes' : 'Close'}
        </Button>
        <Tooltip
          title={dirty ? 'Save changes to allergies and medical conditions' : 'No unsaved changes'}
        >
          <span>
            <Button
              variant="contained"
              startIcon={
                updateMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={!dirty || !profile || updateMutation.isPending || readOnly}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalProfileDialog;
